/**
 * @file interpret/components/callMicDevices.ts
 * @brief Keeps our own capture off Verblizr Microphone (useCallMicDefault.ts).
 *        Pure, so callMicDevices.test.ts runs under node:test.
 *
 * @author DES
 * @date 03-10-2026
 */

import type { MicDevice } from "../../employee-boardroom/capture/useMicDevices";

const OURS = /verblizr/i;

/**
 * Windows has no Verblizr Microphone yet (DES 06-10-2026). To test Call mode
 * there, VB-Cable stands in: we play into "CABLE Input" and the call app's
 * mic is "CABLE Output". Windows only: on a Mac it is never offered.
 */
const WIN_TEST_CABLE = /cable input/i;

/** A device a translation may be sent into the call through. */
export function isCallSendDevice(label: string, platform?: string): boolean {
  return OURS.test(label) || (platform === "win32" && WIN_TEST_CABLE.test(label));
}

/** True when the call device is ours (then it is also the Mac's mic). */
export function sendsToVerblizrMic(sendLabel: string): boolean {
  return OURS.test(sendLabel);
}

/**
 * The mic list for the picker: never Verblizr Microphone, and "System
 * default" named after the mic before ours while ours is the default.
 */
export function micsWithoutCallMic(devices: MicDevice[], previous: string | null): MicDevice[] {
  return devices
    .filter((d) => !(d.deviceId !== "default" && OURS.test(d.label)))
    .map((d) =>
      d.deviceId === "default" && OURS.test(d.label)
        ? { ...d, label: previous ? `Default - ${previous}` : "Default" }
        : d,
    );
}

/**
 * The mic to capture. A chosen mic is kept; "System default" while ours is
 * the default becomes the mic before it (by name), else the first real mic.
 */
export function micForCapture(
  devices: MicDevice[],
  deviceId: string,
  previous: string | null,
  routed: boolean,
  /** Call mode: the headphones translations play on (their mic is close to
   *  your mouth; the MacBook mic hears the whole room). */
  headset?: string | null,
): string {
  if (deviceId) return deviceId;
  const real = devices.filter((d) => d.deviceId !== "default" && !OURS.test(d.label));
  // "System default" in Call mode with headphones that have a mic (EarPods,
  // AirPods, a headset): use theirs (DES, 04-10-2026: room noise in the
  // MacBook mic kept the other person's turns open).
  if (headset) {
    const stem = headset
      .replace(/\s*\(.*\)\s*$/, "")
      .trim()
      .toLowerCase();
    const mic = stem ? real.find((d) => d.label.toLowerCase().startsWith(stem)) : undefined;
    if (mic) return mic.deviceId;
  }
  if (!routed) return deviceId;
  // Before the switch (Start comes first), the Mac's default is still yours.
  const current = devices.find((d) => d.deviceId === "default");
  const name =
    previous ??
    (current && !OURS.test(current.label) ? current.label.replace(/^default\s*-\s*/i, "") : null);
  const byName = name ? real.find((d) => d.label === name || d.label.startsWith(name)) : null;
  return (byName ?? real[0])?.deviceId ?? "";
}

// ---- where you hear their translation (Call mode) ---------------------------

export type OutputDevice = { deviceId: string; label: string };

/** Devices that carry sound into an app, not to your ears. */
const VIRTUAL = /verblizr|virtual|teams audio|zoom ?audio|blackhole|loopback|soundflower/i;
const HEADPHONES = /headphone|airpods|buds|headset|earphone|beats|bose|jabra|wh-|wf-/i;
/** Not headphones: the Mac's own speakers, a monitor, a TV. */
const ROOM = /speaker|display|hdmi|\btv\b|built-in|communications/i;

/** Outputs you can hear translations on (no virtual devices). */
export function hearOptions(outputs: OutputDevice[]): OutputDevice[] {
  return outputs.filter((d) => d.deviceId !== "default" && !VIRTUAL.test(d.label));
}

/**
 * Headphones that are connected now, if any: a name that says so first,
 * else any output that is not the Mac's speakers or a screen (Bluetooth
 * headphones often carry only a model name).
 */
export function connectedHeadphones(outputs: OutputDevice[]): OutputDevice | null {
  const options = hearOptions(outputs);
  return (
    options.find((d) => HEADPHONES.test(d.label)) ??
    options.find((d) => !ROOM.test(d.label)) ??
    null
  );
}

/**
 * The device to hear translations on. `chosen` "" = automatic: headphones
 * when connected, else null (the Mac's sound output). A chosen device that
 * is gone falls back to automatic.
 */
/**
 * Whether translations will sound in the room (the Mac's speakers, a
 * screen): then CallFloor shuts your mic while they play. Unknown counts as
 * the room, the safe side.
 */
export function hearsOnSpeakers(outputs: OutputDevice[], hearDeviceId: string | null): boolean {
  if (!hearDeviceId) return true;
  const d = outputs.find((o) => o.deviceId === hearDeviceId);
  return !d || (ROOM.test(d.label) && !HEADPHONES.test(d.label));
}

export function hearDeviceFor(outputs: OutputDevice[], chosen: string): string | null {
  if (chosen && hearOptions(outputs).some((d) => d.deviceId === chosen)) return chosen;
  return connectedHeadphones(outputs)?.deviceId ?? null;
}
